import {Button, Card, CardBody, CardHeader, Col, Label, Row} from "reactstrap";
import Select from "../../../../../components/Common/Select";
import React, {useState} from "react";
import {useQuery} from "@tanstack/react-query";
import {BrokerService} from "../../../../../services/broker.service";
import {SelectOption} from "../../../Statistic/components/types";
import {pay_methods} from "../../../../../constants/traffic_order";
import {getCrmFilters, IExtendedOrderFilters, OrderFilterKey, OrderMultiSelectKey} from "./types";
import {CountryBoxService} from "../../../../../services/country_box.service";
import {UserService} from "../../../../../services/user.service";

interface FilterFieldConfig {
    key: OrderFilterKey;
    label: string;
    placeholder: string;
}

const FILTER_FIELDS: Record<OrderMultiSelectKey, FilterFieldConfig> = {
    brokerIds: {key: "brokerIds", label: "Brokers", placeholder: "All brokers"},
    countryIds: {key: "countryIds", label: "GEO", placeholder: "All GEOs"},
    payMethodIds: {key: "payMethodIds", label: "Pay method", placeholder: "All pay methods"},
    responsibleIds: {key: "responsibleIds", label: "Responsible", placeholder: "All responsible"},
};

interface FilterOrderProps {
    crmId: number;
    extendedFilters: IExtendedOrderFilters;
    onExtendedFiltersChange: (filters: IExtendedOrderFilters) => void;
}

const FilterOrder: React.FC<FilterOrderProps> = ({crmId, extendedFilters, onExtendedFiltersChange}) => {
    const [isFilterOpen, setIsFilterOpen] = useState(false);

    const filterKeys = getCrmFilters();
    const has = (key: OrderFilterKey) => filterKeys.includes(key);

    const onToggleFilters = () => {
        setIsFilterOpen(prevState => !prevState);
    }

    const selectCountries = (countries: any) =>
        countries.data.map((cb: any) => ({value: cb.id, label: cb.title}))

    const {data: countryOptions, isLoading: isLoadingCountries} = useQuery({
        queryKey: ["keyCountries"], queryFn: CountryBoxService.getCountries,
        select: selectCountries,
        enabled: isFilterOpen && has("countryIds"),
    })

    const {data: usersOptions, isLoading: isLoadingUsers} = useQuery({
        queryKey: ["keyUsersAll"], queryFn: UserService.getUsersAll,
        enabled: isFilterOpen && has("responsibleIds"),
    })

    const {data: brokerOptions, isLoading: isLoadingBrokers} = useQuery({
        queryKey: ["keyBrokerList", crmId], queryFn: () => BrokerService.getBrokerByCrmIdList(crmId),
        enabled: isFilterOpen && has("brokerIds"),
    });

    const payMethodOptions = pay_methods.map(pm => ({...pm, label: pm.label.toUpperCase()}))

    const optionsByKey: Record<OrderMultiSelectKey, { options: SelectOption[]; isLoading?: boolean }> = {
        brokerIds: {options: brokerOptions ?? [], isLoading: isLoadingBrokers},
        countryIds: {options: countryOptions ?? [], isLoading: isLoadingCountries},
        responsibleIds: {options: usersOptions ?? [], isLoading: isLoadingUsers},
        payMethodIds: {options: payMethodOptions},
    };

    const getSelected = (key: OrderMultiSelectKey): SelectOption[] => {
        const ids = extendedFilters[key] ?? [];
        return optionsByKey[key].options.filter(o => ids.includes(o.value));
    };

    const onFilterChange = (key: OrderFilterKey, value: SelectOption[] | null) => {
        onExtendedFiltersChange({
            ...extendedFilters, [key]: (value ?? []).map(v => v.value)
        });
    };

    const onResetFilters = () => {
        onExtendedFiltersChange({});
    };

    const hasFilters = filterKeys.some(key => (extendedFilters[key] ?? []).length > 0);

    if (!filterKeys.length) return null;

    return <Row className="align-items-center">
        <Col md={12}>
            <Card className="card-h-100">
                <CardHeader className="py-3">
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                        <div>
                            <h4 className="card-title mb-1">Filters</h4>
                        </div>
                        <Button
                            color="link"
                            className="p-0 text-decoration-none"
                            onClick={onToggleFilters}
                        >
                            {isFilterOpen ? "Hide filters" : "Show filters"}
                        </Button>
                    </div>
                </CardHeader>
                {isFilterOpen && (
                    <CardBody>
                        <Row className="g-3">
                            {filterKeys.map(key => {
                                const cfg = FILTER_FIELDS[key];
                                const {options, isLoading} = optionsByKey[key];
                                return (
                                    <Col xl={3} md={6} key={key}>
                                        <Label className="form-label">{cfg.label}</Label>
                                        <Select
                                            isMulti
                                            isClearable
                                            placeholder={cfg.placeholder}
                                            options={options}
                                            value={getSelected(key)}
                                            onChange={(v: any) => onFilterChange(key, v)}
                                            isLoading={isLoading}
                                            isDisabled={!options.length || isLoading}
                                        />
                                    </Col>
                                );
                            })}

                            <Col xs={12} className="d-flex justify-content-end">
                                <Button
                                    color="light"
                                    outline
                                    onClick={onResetFilters}
                                    disabled={!hasFilters}
                                >
                                    Reset filters
                                </Button>
                            </Col>
                        </Row>
                    </CardBody>
                )}
            </Card>
        </Col>
    </Row>
}

export default FilterOrder;